// @flow
import React, { FC, useState, ReactNode } from 'react';
import MUIMenu, { MenuClasses } from '@mui/material/Menu';
import MUIMenuItem from '@mui/material/MenuItem';
import MUIIconButton from '@mui/material/IconButton';
import { SxProps, Theme } from '@mui/material/styles';
import Button from '../Button';

export interface StyledMenuPropsType {
    id: string;
    anchorEl?: Element | null;
    open: boolean;
    keepMounted: boolean;
    classes: Partial<MenuClasses> | undefined;
    variant?: 'menu' | 'selectedMenu' | undefined;
    className?: string | undefined;
    onClose: () => void;
    children: ReactNode[];
    sx?: SxProps<Theme>;
}

const StyledMenu: FC<StyledMenuPropsType> = (props) => {
    return (
        <MUIMenu
            keepMounted={props.keepMounted}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            id={props.id}
            anchorEl={props.anchorEl}
            open={props.open}
            onClose={props.onClose}
            classes={props.classes}
            variant={props.variant}
            className={props.className}
            sx={props.sx}
        >
            {props.children}
        </MUIMenu>
    );
};

export interface MenuPropsType {
    inline?: boolean;
    className?: string | undefined;
    text?: string | ReactNode;
    tooltiop?: string;
    icon?: ReactNode;
    items?: ReactNode[];
    itemStyle?: React.CSSProperties | undefined;
    itemClassName?: string | undefined;
    classes?: Partial<MenuClasses> | undefined;
    variant?: 'menu' | 'selectedMenu' | undefined;
    children?: ReactNode;
    sx?: SxProps<Theme>;
    iconSx?: SxProps<Theme>;
    textSx?: SxProps<Theme>;
    buttonVariant?: 'text' | 'outlined' | 'contained';
}

const Menu: FC<MenuPropsType> = (props) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpen = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        if (anchorEl) setAnchorEl(null);
    };

    return (
        <div style={{ display: props.inline ? 'inline-block' : 'block' }} className={props.className}>
            {props.text && (
                <Button
                    aria-controls="fade-menu"
                    aria-haspopup="true"
                    onClick={handleOpen}
                    variant={props.buttonVariant}
                    tooltiop={props.tooltiop}
                    sx={props.textSx}
                >
                    {props.text}
                </Button>
            )}
            {props.icon && (
                <MUIIconButton
                    size="small"
                    aria-controls="fade-menu"
                    aria-haspopup="true"
                    onClick={handleOpen}
                    sx={props.iconSx}
                >
                    {props.icon}
                </MUIIconButton>
            )}
            <StyledMenu
                id="fade-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                classes={props.classes}
                variant={props.variant}
                className={props.className}
                keepMounted
                sx={props.sx}
            >
                {props.items &&
                    props.items.map(
                        (item, key: number): ReactNode => (
                            <MUIMenuItem
                                key={String(key)}
                                onClick={handleClose}
                                style={props.itemStyle}
                                className={props.itemClassName}
                            >
                                {item}
                            </MUIMenuItem>
                        )
                    )}
                {props.children || null}
            </StyledMenu>
        </div>
    );
};

export default Menu;
